import type {
  CompanyDetails,
  ExpandRequest,
  ExpandResponse,
  HealthStatus,
  NetworkOptions,
  NetworkResponse,
  PersonProfile,
  SearchResponse,
} from "../../shared/types";

export class ApiError extends Error {
  constructor(message: string, public status: number) {
    super(message);
  }
}

/** Build estática (GitHub Pages): sem backend, consulta as fontes direto do navegador. */
export const IS_STATIC_BUILD = import.meta.env.VITE_STATIC === "1";

function asApiError(error: unknown): ApiError {
  return new ApiError(error instanceof Error ? error.message : "Falha na consulta.", 502);
}

async function request<T>(url: string, init?: RequestInit, signal?: AbortSignal): Promise<T> {
  const response = await fetch(url, {
    headers: { "Content-Type": "application/json" },
    signal,
    ...init,
  });
  const payload = (await response.json().catch(() => ({}))) as { message?: string };
  if (!response.ok) {
    throw new ApiError(payload.message ?? `Erro ${response.status}`, response.status);
  }
  return payload as T;
}

export async function apiSearch(
  type: "cnpj" | "company" | "partner" | "cpf" | "auto",
  q: string,
  cpf?: string,
  signal?: AbortSignal,
): Promise<SearchResponse> {
  bumpStats();
  if (IS_STATIC_BUILD) {
    const { staticSearch } = await import("./staticApi");
    return staticSearch(type, q, cpf).catch((error) => {
      throw asApiError(error);
    });
  }
  return request("/api/search", { method: "POST", body: JSON.stringify({ type, q, cpf }) }, signal);
}

export async function apiCompany(cnpj: string, signal?: AbortSignal): Promise<CompanyDetails> {
  bumpStats();
  if (IS_STATIC_BUILD) {
    const { staticCompany } = await import("./staticApi");
    return staticCompany(cnpj).catch((error) => {
      throw asApiError(error);
    });
  }
  return request(`/api/company?cnpj=${encodeURIComponent(cnpj)}`, undefined, signal);
}

export async function apiPerson(
  name: string,
  maskedDocument?: string,
  signal?: AbortSignal,
  /** CNPJ da ficha de onde o visitante clicou — vínculo já lido, que a busca por nome pode não ter */
  origem?: string,
): Promise<PersonProfile> {
  bumpStats();
  if (IS_STATIC_BUILD) {
    const { staticPerson } = await import("./staticApi");
    return staticPerson(name, maskedDocument).catch((error) => {
      throw asApiError(error);
    });
  }
  const params = new URLSearchParams({ nome: name });
  if (maskedDocument) params.set("doc", maskedDocument);
  if (origem) params.set("de", origem);
  return request(`/api/person?${params.toString()}`, undefined, signal);
}

export async function apiNetwork(cnpj: string, options: Partial<NetworkOptions>, signal?: AbortSignal): Promise<NetworkResponse> {
  bumpStats();
  if (IS_STATIC_BUILD) {
    const { staticNetwork } = await import("./staticApi");
    return staticNetwork(cnpj, options).catch((error) => {
      throw asApiError(error);
    });
  }
  return request("/api/network", { method: "POST", body: JSON.stringify({ cnpj, ...options }) }, signal);
}

export async function apiExpand(payload: ExpandRequest, signal?: AbortSignal): Promise<ExpandResponse> {
  bumpStats();
  if (IS_STATIC_BUILD) {
    const { staticExpand } = await import("./staticApi");
    return staticExpand(payload).catch((error) => {
      throw asApiError(error);
    });
  }
  return request("/api/network/expand", { method: "POST", body: JSON.stringify(payload) }, signal);
}

export async function apiHealth(signal?: AbortSignal): Promise<HealthStatus> {
  if (IS_STATIC_BUILD) {
    const { staticHealth } = await import("./staticApi");
    return staticHealth();
  }
  return request("/api/health", undefined, signal);
}

// -- Contador de consultas (somente no navegador) ----------------------------

const STATS_KEY = "teia:stats";

export type UsageStats = { total: number; session: number };

export function readStats(): UsageStats {
  let total = 0;
  let session = 0;
  try {
    total = Number(localStorage.getItem(STATS_KEY)) || 0;
    session = Number(sessionStorage.getItem(STATS_KEY)) || 0;
  } catch {
    // sem armazenamento disponível
  }
  return { total, session };
}

function bumpStats(): void {
  try {
    localStorage.setItem(STATS_KEY, String((Number(localStorage.getItem(STATS_KEY)) || 0) + 1));
    sessionStorage.setItem(STATS_KEY, String((Number(sessionStorage.getItem(STATS_KEY)) || 0) + 1));
    window.dispatchEvent(new CustomEvent("teia:stats"));
  } catch {
    // sem armazenamento disponível
  }
}

// -- Histórico local (somente no navegador) ---------------------------------

export type RecentEntry = { cnpj: string; name: string; visitedAt: number };

const RECENT_KEY = "teia:recent";

export function readRecent(): RecentEntry[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const parsed = raw ? (JSON.parse(raw) as RecentEntry[]) : [];
    return Array.isArray(parsed) ? parsed.slice(0, 8) : [];
  } catch {
    return [];
  }
}

export function clearRecent(): void {
  try {
    localStorage.removeItem(RECENT_KEY);
  } catch {
    // sem armazenamento local
  }
}

export function pushRecent(entry: Omit<RecentEntry, "visitedAt">): void {
  try {
    const current = readRecent().filter((item) => item.cnpj !== entry.cnpj);
    current.unshift({ ...entry, visitedAt: Date.now() });
    localStorage.setItem(RECENT_KEY, JSON.stringify(current.slice(0, 8)));
  } catch {
    // sem armazenamento local; o histórico apenas não persiste
  }
}
